import React from 'react';
import { CgSpinner } from 'react-icons/cg';
import Select from 'react-select';

import './OnCallReportDateFilter.css';

const termRangeSelectStyles = {
    control: (baseStyles, state) => ({
        ...baseStyles,
        minHeight: '42px',
        borderColor: state.isFocused ? '#c48e38' : 'rgba(112, 111, 111, 0.25)',
        borderRadius: '10px',
        boxShadow: state.isFocused ? '0 0 0 1px rgba(196, 142, 56, 0.25)' : 'none',
        fontFamily: 'Verdana, Geneva, sans-serif',
        fontSize: '15px',
        '&:hover': {
            borderColor: '#c48e38',
        },
    }),
    option: (baseStyles, state) => ({
        ...baseStyles,
        backgroundColor: state.isSelected ? '#f9b233' : state.isFocused ? 'rgba(249, 178, 51, 0.16)' : '#ffffff',
        color: state.isSelected ? '#ffffff' : '#706f6f',
        fontFamily: 'Verdana, Geneva, sans-serif',
        fontSize: '14px',
    }),
    placeholder: (baseStyles) => ({
        ...baseStyles,
        color: '#706f6f',
    }),
    singleValue: (baseStyles) => ({
        ...baseStyles,
        color: '#706f6f',
    }),
    groupHeading: (baseStyles) => ({
        ...baseStyles,
        backgroundColor: '#f9b233',
        color: '#ffffff',
        fontFamily: 'Verdana, Geneva, sans-serif',
        fontSize: '13px',
        fontWeight: 700,
        margin: '6px 0 4px 0',
        padding: '8px 12px',
        textTransform: 'none',
    }),
};

function formatDateForInput(dateValue) {
    return dateValue.toISOString().split('T')[0];
}

function getCurrentAcademicYearOption() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const septemberFirstThisYear = new Date(currentYear, 8, 1);
    const academicYearStart = today >= septemberFirstThisYear
        ? septemberFirstThisYear
        : new Date(currentYear - 1, 8, 1);

    return {
        value: 'this-academic-year',
        label: 'This Academic Year',
        startDate: formatDateForInput(academicYearStart),
        endDate: formatDateForInput(today),
    };
}

function buildTermRangeOptions(termDateRanges) {
    const terms = termDateRanges?.terms;
    const academicYearOption = getCurrentAcademicYearOption();
    if (!terms || typeof terms !== 'object') {
        return [academicYearOption];
    }

    const termOptions = Object.values(terms).map((term) => {
        const termOption = {
            value: `term-${term.name}`,
            label: `${term.name} (Full Term)`,
            startDate: term.startDate,
            endDate: term.endDate,
        };
        const halfTermOptions = Object.values(term?.halfTerms || {}).map((halfTerm) => ({
            value: `half-term-${halfTerm.name}`,
            label: halfTerm.name,
            startDate: halfTerm.startDate,
            endDate: halfTerm.endDate,
        }));

        return {
            label: term.name,
            options: [termOption, ...halfTermOptions],
        };
    });

    return [academicYearOption, ...termOptions];
}

export default function OnCallReportDateFilter({
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    onApply,
    isLoading,
    termDateRanges,
    selectedTermRange,
    onTermRangeChange,
}) {
    const termRangeOptions = buildTermRangeOptions(termDateRanges);

    return (
        <div className='oncall_reportDateFilter'>
            <div className='oncall_reportDateFilter_inputs'>
                <label className='oncall_reportDateFilter_field oncall_reportDateFilter_termField'>
                    <span className='oncall_reportDateFilter_label'>Term Range Selector</span>
                    <Select
                        className='oncall_reportDateFilter_select'
                        classNamePrefix='oncall_reportDateFilter_select'
                        value={selectedTermRange}
                        options={termRangeOptions}
                        onChange={onTermRangeChange}
                        isClearable
                        isDisabled={isLoading || termRangeOptions.length === 0}
                        placeholder='Select term range'
                        styles={termRangeSelectStyles}
                    />
                </label>

                <label className='oncall_reportDateFilter_field'>
                    <span className='oncall_reportDateFilter_label'>Start Date</span>
                    <input
                        type='date'
                        className='oncall_reportDateFilter_input'
                        value={startDate}
                        max={endDate}
                        onChange={(event) => setStartDate(event.target.value)}
                    />
                </label>

                <label className='oncall_reportDateFilter_field'>
                    <span className='oncall_reportDateFilter_label'>End Date</span>
                    <input
                        type='date'
                        className='oncall_reportDateFilter_input'
                        value={endDate}
                        min={startDate}
                        onChange={(event) => setEndDate(event.target.value)}
                    />
                </label>

                <div className='oncall_reportDateFilter_field oncall_reportDateFilter_buttonField'>
                    <span className='oncall_reportDateFilter_label oncall_reportDateFilter_labelPlaceholder'>Apply</span>
                    <button
                        type='button'
                        className='oncall_reportDateFilter_button'
                        onClick={onApply}
                        disabled={isLoading}
                    >
                        {isLoading ? <CgSpinner className='oncall_reportDateFilter_spinner' /> : 'Apply Date Filter'}
                    </button>
                </div>
            </div>
        </div>
    );
}
